import React, { useState, useRef } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { useVaultItems, useUploadToVault, useDeleteVaultItem } from '../hooks/useVault';
import { VaultItem, VaultItemVisibility } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { RichContentViewer } from '../components/RichContentViewer';
import { 
  Lock, 
  FileText, 
  Search, 
  Plus, 
  Trash2, 
  Download, 
  Shield, 
  Eye, 
  EyeOff,
  Users,
  ArrowUp,
  ArrowDown,
  Globe,
  HardDrive,
  FileImage,
  FileBarChart,
  File as FileIcon,
  UploadCloud,
  X
} from 'lucide-react';
import { cn, safeFormat } from '../lib/utils';
import { ProgressBar } from '../components/ui/ProgressBar';
import { TIER_LIMITS } from '../constants';

const VisibilityBadge: React.FC<{ visibility: VaultItemVisibility }> = ({ visibility }) => {
  const { node } = useTenant();
  const terms = node?.config?.terminology;

  switch (visibility) {
    case 'PRIVATE':
      return (
        <Badge variant="gray" className="flex items-center gap-1">
          <Lock size={10} />
          <span>Local Only</span>
        </Badge>
      );
    case 'SHARED_UP':
      return (
        <Badge variant="blue" className="flex items-center gap-1">
          <ArrowUp size={10} />
          <span>Shared with {terms?.parentNode || 'Superiors'}</span>
        </Badge>
      );
    case 'SHARED_DOWN':
      return (
        <Badge variant="purple" className="flex items-center gap-1">
          <ArrowDown size={10} />
          <span>Shared with {terms?.childNode ? `${terms.childNode}s` : 'Children'}</span>
        </Badge>
      );
    case 'GLOBAL':
      return (
        <Badge variant="green" className="flex items-center gap-1">
          <Globe size={10} />
          <span>Global</span>
        </Badge>
      );
    default:
      return null;
  }
};

function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + ' B';
  else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(fileType: string) {
  const type = (fileType || '').toLowerCase();
  if (type.includes('image')) return <FileImage size={24} />;
  if (type.includes('pdf')) return <FileText size={24} />;
  if (type.includes('excel') || type.includes('spreadsheet') || type.includes('csv')) return <FileBarChart size={24} />;
  return <FileIcon size={24} />;
}

export const VaultPage = () => {
  const { node } = useTenant();
  const { can, role } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewItem, setPreviewItem] = useState<VaultItem | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  const { data: items = [], isLoading } = useVaultItems(node?.id || '');
  const uploadMutation = useUploadToVault(node?.id || '');
  const deleteMutation = useDeleteVaultItem(node?.id || '');

  const tierLimits = TIER_LIMITS[node?.tier || 'LOCAL'];
  const storageUsed = node?.storageUsedBytes || 0;
  const storageLimit = tierLimits.storageBytes;
  const storagePercentage = Math.min((storageUsed / storageLimit) * 100, 100);
  const isOverLimit = storageUsed >= storageLimit;

  const filteredItems = items.filter(item => 
    (item.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.fileName || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedFile) return;

    if (storageUsed + selectedFile.size > storageLimit) {
      alert('Upload failed: Storage quota exceeded.');
      return;
    }

    const formData = new FormData(e.currentTarget);
    
    uploadMutation.mutate({
      file: selectedFile,
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      visibility: formData.get('visibility') as VaultItemVisibility,
    }, {
      onSuccess: () => {
        setIsModalOpen(false);
        setSelectedFile(null);
      }
    });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to remove this item from the vault?')) {
      deleteMutation.mutate(id);
    }
  };

  const openPreview = async (item: VaultItem) => {
    setPreviewItem(item);
    setIsLoadingPreview(true);
    try {
      // Very basic preview fetching for markdown/text
      const textTypes = ['text/plain', 'text/markdown', 'application/json', 'text/csv'];
      if (textTypes.includes((item.fileType || '').toLowerCase()) || item.fileName.endsWith('.md') || item.fileName.endsWith('.txt')) {
        let content = '';
        if (item.fileUrl) {
           const res = await fetch(item.fileUrl);
           content = await res.text();
        } else {
           content = 'No preview available.';
        }
        setPreviewContent(content);
      } else {
        setPreviewContent('');
      }
    } catch (e) {
      setPreviewContent('Failed to load preview.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 font-medium">Securing access to the vault...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">The Vault</h1>
          <p className="text-xs md:text-sm text-gray-500">Secure document storage and hierarchical asset sharing.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="hidden lg:flex flex-col items-end mr-4 min-w-[120px]">
             <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
               <HardDrive size={10} className={cn(isOverLimit ? "text-red-500" : "text-gray-400")} />
               <span>{(storageUsed / 1024 / 1024).toFixed(1)}MB / {(storageLimit / 1024 / 1024).toFixed(0)}MB Total Capacity</span>
             </div>
             <div className="w-full">
               <ProgressBar 
                 value={storagePercentage} 
                 variant={isOverLimit ? 'amber' : storagePercentage > 85 ? 'amber' : 'blue'} 
               />
             </div>
          </div>

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input 
              placeholder="Search files..." 
              className="pl-10 w-48 md:w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {(can('VAULT_UPLOAD') || can('manage_vault')) && (
            <Button 
              variant="primary" 
              onClick={() => { setIsModalOpen(true); setSelectedFile(null); }}
              disabled={isOverLimit}
              className={cn(isOverLimit && "opacity-50 cursor-not-allowed")}
            >
              <Plus size={18} />
              <span className="hidden md:inline">
                {isOverLimit ? 'Quota Exceeded' : 'Upload Asset'}
              </span>
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item) => {
          const isOwner = item.nodeId === node?.id;
          
          return (
            <Card key={item.id} className="group relative">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-blue-50 text-blue-700 rounded-none">
                  {getFileIcon(item.fileType)}
                </div>
                <div className="flex items-center gap-2">
                  <VisibilityBadge visibility={item.visibility} />
                </div>
              </div>

              <div className="space-y-1 mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate" title={item.title}>
                  {item.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 min-h-[32px]">
                  {item.description || "No description provided."}
                </p>
              </div>

              <div className="flex flex-col gap-3 py-3 border-t border-black dark:border-white">
                <div className="flex items-center justify-between text-[10px] uppercase font-bold tracking-widest text-gray-400 font-mono">
                  <span>File Details</span>
                  <span>{formatFileSize(item.fileSize)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-none bg-white flex items-center justify-center text-[10px] font-bold text-gray-400 dark:bg-slate-800">
                      {(item.uploadedByName || '?')[0]}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{item.uploadedByName}</span>
                  </div>
                  <span className="text-[10px] text-gray-400 font-mono">
                    {safeFormat(item.createdAt, 'MMM d, yyyy')}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-2">
                <Button variant="secondary" size="sm" className="flex-1 gap-2" onClick={() => window.open(item.fileUrl, '_blank')}>
                  <Download size={14} />
                  Download
                </Button>
                <Button variant="secondary" size="sm" className="px-3" onClick={() => openPreview(item)} title="Preview">
                  <Eye size={14} />
                </Button>
                
                {isOwner && (can('VAULT_MANAGE') || can('manage_vault')) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 px-2"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>

              {!isOwner && (
                <div className="mt-3 flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">
                  <ArrowUp size={10} className="text-blue-500" />
                  <span>Origin: {item.nodeName}</span>
                </div>
              )}
            </Card>
          );
        })}

        {filteredItems.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 py-20 bg-white border-2 border-black dark:border-white border-dashed border-black dark:border-white rounded-none flex flex-col items-center justify-center text-center dark:bg-slate-900">
            <div className="w-16 h-16 bg-white rounded-none flex items-center justify-center text-gray-300 mb-4 dark:bg-slate-950">
              <Shield size={32} />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">No assets found</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Your search did not match any files in your vault or shared lineage.
            </p>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Upload Asset to Vault"
      >
        <form onSubmit={handleUpload} className="space-y-4">
          <Input label="Title" name="title" placeholder="e.g. Q1 Financial Report" required />
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
            <textarea 
              name="description" 
              className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none focus:outline-none focus:border-blue-500 min-h-[80px]"
              placeholder="Briefly describe what this document contains..."
            />
          </div>

          <div>
             <label className="block text-xs font-semibold text-gray-700 mb-1">File Attachment (Required)</label>
             <div 
               className={cn("border-2 border-dashed rounded-none p-6 flex flex-col items-center justify-center transition-colors cursor-pointer text-center", selectedFile ? "bg-blue-50 border-blue-200" : "bg-white border-black dark:border-white hover:bg-white dark:bg-slate-950")}
               onClick={() => fileInputRef.current?.click()}
             >
               <input type="file" required className="hidden" ref={fileInputRef} onChange={handleFileSelect} />
               {!selectedFile ? (
                 <>
                   <UploadCloud size={32} className="text-gray-400 mb-2" />
                   <p className="text-sm font-medium text-gray-700 mb-1">Click to browse or drag and drop</p>
                   <p className="text-xs text-gray-500 dark:text-gray-400 max-w-[250px]">PDF, Word, Excel, Images up to {(storageLimit / 1024 / 1024).toFixed(0)}MB</p>
                 </>
               ) : (
                 <div className="flex flex-col items-center">
                   <div className="p-3 bg-blue-100 text-blue-600 rounded-none mb-3">
                     {getFileIcon(selectedFile.type)}
                   </div>
                   <p className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate max-w-[250px]">{selectedFile.name}</p>
                   <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatFileSize(selectedFile.size)}</p>
                   <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }} className="mt-3 text-xs font-bold text-red-500 hover:text-red-700 uppercase tracking-widest">Remove File</button>
                 </div>
               )}
             </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Visibility Scope</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex items-center gap-3 p-3 border-2 border-black dark:border-white rounded-none cursor-pointer hover:border-blue-200 transition-colors has-[:checked]:bg-blue-50 has-[:checked]:border-blue-500 group">
                <input type="radio" name="visibility" value="PRIVATE" defaultChecked className="hidden" />
                <div className="p-2 bg-white rounded-none border-2 border-black dark:border-white group-has-[:checked]:text-blue-600 dark:bg-slate-900">
                  <Lock size={16} />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-900 dark:text-gray-100 leading-tight">Local Only</span>
                  <span className="text-[10px] text-gray-500">Private to this group</span>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border-2 border-black dark:border-white rounded-none cursor-pointer hover:border-blue-200 transition-colors has-[:checked]:bg-blue-50 has-[:checked]:border-blue-500 group">
                <input type="radio" name="visibility" value="SHARED_UP" className="hidden" />
                <div className="p-2 bg-white rounded-none border-2 border-black dark:border-white group-has-[:checked]:text-blue-600 dark:bg-slate-900">
                  <ArrowUp size={16} />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-900 dark:text-gray-100 leading-tight">Shared Upward</span>
                  <span className="text-[10px] text-gray-500">Visible to superior orgs</span>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border-2 border-black dark:border-white rounded-none cursor-pointer hover:border-blue-200 transition-colors has-[:checked]:bg-blue-50 has-[:checked]:border-blue-500 group">
                <input type="radio" name="visibility" value="SHARED_DOWN" className="hidden" />
                <div className="p-2 bg-white rounded-none border-2 border-black dark:border-white group-has-[:checked]:text-blue-600 dark:bg-slate-900">
                  <ArrowDown size={16} />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-900 dark:text-gray-100 leading-tight">Shared Downward</span>
                  <span className="text-[10px] text-gray-500">Visible to subgroups</span>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border-2 border-black dark:border-white rounded-none cursor-pointer hover:border-blue-200 transition-colors has-[:checked]:bg-blue-50 has-[:checked]:border-blue-500 group">
                <input type="radio" name="visibility" value="GLOBAL" className="hidden" />
                <div className="p-2 bg-white rounded-none border-2 border-black dark:border-white group-has-[:checked]:text-blue-600 dark:bg-slate-900">
                  <Globe size={16} />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-900 dark:text-gray-100 leading-tight">Global Branch</span>
                  <span className="text-[10px] text-gray-500">Visible to everyone in chain</span>
                </div>
              </label>
            </div>
          </div>

          <div className="p-4 bg-amber-50 rounded-none border-2 border-black dark:border-white border-amber-100 flex gap-3">
            <Shield className="text-amber-600 shrink-0" size={18} />
            <p className="text-[10px] text-amber-800 leading-relaxed font-medium capitalize">
              Ensure you have the rights to share this document according to your organization's privacy policy.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button variant="primary" type="submit" isLoading={uploadMutation.isPending} disabled={!selectedFile}>
              Upload to Vault
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!previewItem} onClose={() => setPreviewItem(null)} title={previewItem?.title || 'Preview'} className="max-w-4xl">
        <div className="bg-white rounded-none border-2 border-black dark:border-white overflow-hidden relative min-h-[400px] flex items-center justify-center p-6 dark:bg-slate-950">
           {isLoadingPreview ? (
              <Spinner />
           ) : previewItem && (previewItem.fileType || '').includes('image') ? (
              <img src={previewItem.fileUrl} alt={previewItem.title} className="max-w-full max-h-[70vh] object-contain rounded drop-shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]" />
           ) : previewContent ? (
              <div className="w-full bg-white p-8 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-y-auto max-h-[60vh] text-left dark:bg-slate-900">
                <RichContentViewer content={previewContent} />
              </div>
           ) : (
              <div className="text-center text-gray-500">
                <FileIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <p>No preview available for this file type.</p>
                <div className="mt-4">
                  <Button variant="primary" onClick={() => window.open(previewItem?.fileUrl, '_blank')}>
                    <Download size={16} className="mr-2" /> Download to View
                  </Button>
                </div>
              </div>
           )}
        </div>
      </Modal>
    </div>
  );
};

